import React from "react";
import { ArrowRight } from "lucide-react";
import BrandLogo from "@/assets/svg/White-logo.svg";
import SquircleIcon from "@/shared/components/ui/SquircleIcon";
import CommonTextArea from "@/shared/components/ui/Input/CommonTextArea";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SavedSearchList from "./SavedSearchList";
import ExampleSearches from "./ExampleSearches";
import CampaignSearchPicker from "./CampaignSearchPicker";
import type { SavedSearchItem, SearchCampaign } from "../../types";

const SquircleIconComponent = SquircleIcon as React.ComponentType<any>;
const CommonTextAreaComponent = CommonTextArea as React.ComponentType<any>;
const PrimaryButtonComponent = PrimaryButton as React.ComponentType<any>;

interface ProspectSearchHomeProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  isLoading: boolean;
  savedSearches: SavedSearchItem[];
  onDeleteSavedSearch: (id: string | number) => void;
  onCampaignSearch: (campaign: SearchCampaign) => void;
  /** Runs a saved search: its query, or its campaign's ICP for a campaign search. */
  onRunSaved: (search: SavedSearchItem) => void;
}

/** The empty-state screen: query box, saved searches and example searches. */
export default function ProspectSearchHome({
  value,
  onChange,
  onSearch,
  isLoading,
  savedSearches,
  onDeleteSavedSearch,
  onCampaignSearch,
  onRunSaved,
}: ProspectSearchHomeProps) {
  return (
    // The card is capped at the panel's height and centred; the saved-search list shrinks
    // and scrolls on its own, so the page itself does not scroll (same as Find accounts).
    <div className="flex h-full w-full justify-center px-6 py-8">
      <div className="my-auto flex max-h-full min-h-0 w-full max-w-[740px] flex-col gap-8">
        <div className="flex shrink-0 flex-col gap-3">
          <SquircleIconComponent size="w-11 h-11" className="flex self-center">
            <img src={BrandLogo} alt="brand-logo" height={31} width={19} />
          </SquircleIconComponent>
          <h3 className="text-2xl font-medium text-black dark:text-white font-sans text-center">
            Which contacts are you looking for?
          </h3>
        </div>

        <div className="relative w-full shrink-0">
          <CommonTextAreaComponent
            value={value}
            placeholder="Describe the decision makers you want to find (e.g. Sales Directors in Europe at B2B SaaS companies)..."
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSearch(value);
              }
            }}
            rows={4}
            className="w-full px-4 py-4 pb-16 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-2xl"
            textAreaClassName="px-0 py-0 placeholder:text-gray-400 placeholder:text-sm"
          />
          <div className="absolute bottom-4 right-4">
            <PrimaryButtonComponent
              startContent={false}
              endContent={<ArrowRight size={16} className="text-white" />}
              onClick={() => onSearch(value)}
              isLoading={isLoading}
              disabled={!value.trim() || isLoading}
            >
              {isLoading ? "Finding Contacts..." : "Find Contacts"}
            </PrimaryButtonComponent>
          </div>
        </div>

        <div className="shrink-0">
          <CampaignSearchPicker onPick={onCampaignSearch} disabled={isLoading} />
        </div>

        {/* Examples are only for someone who has not searched yet (same as Account Discovery). */}
        {savedSearches.length > 0 ? (
          <SavedSearchList searches={savedSearches} onRun={onRunSaved} onDelete={onDeleteSavedSearch} />
        ) : (
          <ExampleSearches onPick={onSearch} />
        )}
      </div>
    </div>
  );
}
